import {compact} from '@benchling/js-utils';
import React from 'react';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {NO_ACCESS_TEXT} from 'processes/ProcessConstants';
import {ChipSize} from 'ui-library/chips/Chip';
import {ChipPopoverInfoField} from 'ui-library/chips/ChipPopoverContent';
import WorksheetChip from 'ui-library/chips/WorksheetChip';

import {GetChipPopoverContentsArgs} from '../ChipUtilConstants';
import {
  getAssigneeField,
  getIdField,
  getNameField,
  getScheduledOnField,
  getStatusField,
  getStudiesField,
} from '../getDefaultProcessTaskChipPopoverContent';
import {ProcessTaskForPopoverGql} from '../graphql';
import {getProcedureChipPopoverFields} from './getCommonFields';

type ProcessTaskMethodExecutionInstanceOrInaccessibleGql =
  ProcessTaskForPopoverGql['procedureMethodExecutionInstance'];
type ProcessTaskMethodExecutionInstanceGql = ExtractByTypename<
  ProcessTaskForPopoverGql['procedureMethodExecutionInstance'],
  'ProcedureMethodExecutionInstanceNode'
>;
type ProcessTaskMethodExecutionInstanceProcedureRunGql = ExtractByTypename<
  ProcessTaskMethodExecutionInstanceGql['procedureRun'],
  'ProcedureRunNode'
>;

export function extractMethodExecutionInfo(
  methodExecutionInstance: ProcessTaskMethodExecutionInstanceOrInaccessibleGql
): {
  worksheetId: string | null;
  procedureRun: ProcessTaskMethodExecutionInstanceProcedureRunGql | null;
  conditionReplicateRunTaskId: string | null;
} {
  let worksheetId: string | null = null;
  let procedureRun = null;
  let conditionReplicateRunTaskId: string | null = null;
  if (methodExecutionInstance != null && !isInaccessible(methodExecutionInstance)) {
    procedureRun = methodExecutionInstance.procedureRun;
    worksheetId = getAccessibleOrInaccessibleId(methodExecutionInstance.worksheet);
    if (methodExecutionInstance.procedureRunConditionReplicate?.processRootTask != null) {
      conditionReplicateRunTaskId = getAccessibleOrInaccessibleId(
        methodExecutionInstance.procedureRunConditionReplicate?.processRootTask
      );
    }
  }

  return {worksheetId, procedureRun, conditionReplicateRunTaskId};
}

export default function getProcedureUnitOperationChipPopoverContents({
  processTask,
  icon,
  runPopoverField,
  procedureTemplateField,
}: GetChipPopoverContentsArgs & {
  runPopoverField: ChipPopoverInfoField;
  procedureTemplateField: ChipPopoverInfoField;
}): Array<ChipPopoverInfoField> {
  const methodExecutionInstance = processTask.procedureMethodExecutionInstance;
  const {worksheetId} = extractMethodExecutionInfo(methodExecutionInstance);

  return compact([
    getIdField(processTask),
    getNameField(processTask, icon),
    getStatusField(processTask),
    getAssigneeField(processTask),
    getScheduledOnField(processTask),
    {
      label: getProcedureChipPopoverFields().WORKSHEET,
      value: worksheetId != null ? <WorksheetChip id={worksheetId} size={ChipSize.MINI} /> : NO_ACCESS_TEXT,
    },
    runPopoverField,
    procedureTemplateField,
    getStudiesField(processTask),
  ]);
}
